import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { TENANTS_API_ENDPOINTS as cfg } from './config';
import type { GetTenantMemberResponse, ListTenantMembersResponse } from './types';
import type { DefinitionsFromApi, OverrideResultType } from '@reduxjs/toolkit/query';

const tags = ['Tenants', 'TenantMembers'] as const;
type TagTypes = (typeof tags)[number];

type Definitions = DefinitionsFromApi<typeof generatedApi>;
type UpdatedDefinitions = Omit<
  Definitions,
  typeof cfg.listTenantMembers.generated | typeof cfg.getTenantMember.generated
> & {
  [cfg.listTenantMembers.generated]: OverrideResultType<
    Definitions[typeof cfg.listTenantMembers.generated],
    ListTenantMembersResponse['data']
  >;
  [cfg.getTenantMember.generated]: OverrideResultType<
    Definitions[typeof cfg.getTenantMember.generated],
    GetTenantMemberResponse['data']
  >;
};

/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 * All endpoint references are derived from the central definitions in ./config.ts.
 *
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * Two collections are modelled here, with two different "collection sentinel"
 * strategies:
 *
 * 1. Top-level tenants (`Tenants` tag) — the classic LIST pattern. The list
 *    query provides a per-item tag for every tenant plus a dedicated
 *    `{ type: 'Tenants', id: 'LIST' }` tag, so that:
 *    - updating a single tenant invalidates only that tenant's `{ id }` tag,
 *      which refetches the list (the list provides that same per-item tag);
 *    - creating or deleting a tenant invalidates `{ id: 'LIST' }` to refetch
 *      the collection without touching unrelated entities. A delete must not
 *      invalidate the tenant's own tags: its detail page is still subscribed
 *      when the delete succeeds, and refetching a tenant that no longer
 *      exists 404s into a "No tenant found" toast while the page navigates
 *      back to the list (as `deleteSocialPost` in `@skynet/data-smm`).
 *    Note: `getTenant` is keyed by `idOrSlug` (a string), while the list and
 *    the mutations are keyed by the numeric `id`. RTK matches tag ids by strict
 *    equality, so the numeric per-item tag alone would not refetch an open
 *    `getTenant` query (its tag id is the string route param, or the slug).
 *    `updateTenant` therefore invalidates the numeric id (for the list), the
 *    stringified id, and the response `slug` — covering a detail view opened
 *    by either id or slug.
 *
 * 2. Tenant members (`TenantMembers` tag) — a SCOPED sub-collection. Members
 *    only ever exist in the context of a parent tenant, so instead of a single
 *    literal `'LIST'` sentinel there is one collection sentinel *per tenant*:
 *    `{ type: 'TenantMembers', id: <tenantId> }`. The member list provides a
 *    per-member tag (`{ id: `${tenantId}-${membershipId}` }`) plus that
 *    parent-scoped sentinel, and every member mutation invalidates only the
 *    sentinel for its own `tenantId` — so adding/removing a member refetches
 *    that tenant's member list without disturbing other tenants' lists.
 *
 * This replaces the previous manual `dispatch(invalidateTags(...))` /
 * `updateQueryData` workaround in `onQueryStarted` (attributed to
 * redux-toolkit#5093, which is about the `isSuccess` flag during pessimistic
 * updates, not tag invalidation): declarative `invalidatesTags` propagates to
 * the list correctly via the per-item / scoped-sentinel tags.
 */
export const tenantsApi = generatedApi.enhanceEndpoints<TagTypes, UpdatedDefinitions>({
  addTagTypes: tags,
  endpoints: {
    [cfg.listTenants.generated]: {
      providesTags: (result) => provideListTags(result, 'Tenants'),
    },
    [cfg.getTenant.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Tenants', id: arg.idOrSlug }],
    },
    [cfg.createTenant.generated]: {
      invalidatesTags: [{ type: 'Tenants', id: LIST_TAG_ID }],
    },
    [cfg.updateTenant.generated]: {
      // Invalidate the numeric id (matched by the list's per-item tag) plus the
      // stringified id and the response slug, so a `getTenant` detail query —
      // keyed by `idOrSlug` (a string) — refetches whether it was opened by id
      // or by slug. Note: on a slug rename this invalidates the new slug; a
      // detail view opened by the previous slug is intentionally not refetched
      // (a rare, self-inflicted case for the user performing the edit).
      invalidatesTags: (result, _error, arg) => {
        const idTags = [
          { type: 'Tenants' as const, id: arg.id },
          { type: 'Tenants' as const, id: String(arg.id) },
        ];
        return result ? [...idTags, { type: 'Tenants' as const, id: result.slug }] : idTags;
      },
    },
    // The list only: see "Top-level tenants" above for why not the tenant itself.
    [cfg.deleteTenant.generated]: {
      invalidatesTags: [{ type: 'Tenants', id: LIST_TAG_ID }],
    },

    [cfg.listTenantMembers.generated]: {
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.map((member) => ({
                type: 'TenantMembers' as const,
                id: `${arg.tenantId}-${member.id}`,
              })),
              { type: 'TenantMembers', id: arg.tenantId },
            ]
          : [{ type: 'TenantMembers', id: arg.tenantId }],

      transformResponse: (
        response: ListTenantMembersResponse,
        _arg,
        _meta,
      ): ListTenantMembersResponse['data'] => {
        return response.data;
      },
    },
    [cfg.getTenantMember.generated]: {
      // Also provide the parent-scoped collection tag so that the member mutations
      // below — which invalidate `{ TenantMembers, id: tenantId }` (they only carry
      // the tenantId, not a per-member key matching this query's `${tenantId}-${userId}`
      // tag) — refetch an open single-member query for that tenant too.
      providesTags: (_result, _error, arg) => [
        { type: 'TenantMembers', id: `${arg.tenantId}-${arg.userId}` },
        { type: 'TenantMembers', id: arg.tenantId },
      ],
      transformResponse: (
        response: GetTenantMemberResponse,
        _arg,
        _meta,
      ): GetTenantMemberResponse['data'] => {
        return response.data;
      },
    },
    [cfg.addTenantMember.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'TenantMembers', id: arg.tenantId }],
    },
    [cfg.updateTenantMembership.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'TenantMembers', id: arg.tenantId }],
    },
    [cfg.deleteTenantMember.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'TenantMembers', id: arg.tenantId }],
    },
    [cfg.deleteTenantMembership.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'TenantMembers', id: arg.tenantId }],
    },
  },
});

// Re-export hooks and endpoint maps via helpers to match the pattern used in data-roles
export const hookExports: HookExports<typeof cfg, typeof tenantsApi> = createHookExports(
  cfg,
  tenantsApi,
);
export const tenantApiEndpoints = createEndpointExports(cfg, tenantsApi);
